import {
	Autocomplete,
	Chip,
	Stack,
	TextField,
	Typography,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Divider,
	IconButton,
} from "@mui/material";
import {
	DeviceStatus,
	DeviceCategory,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import {
	DeviceForm,
	DeviceFormItem,
} from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useMemo, useState, useRef } from "react";
import React from "react";
import { StatusBadge } from "Shared/Badges/StatusBadge";
import { getDeviceStateColor } from "Infrastructure/Utils/Nsure/DeviceUtils";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
	InstitutionDeviceModel,
	mergeDeviceStatuses,
	ProductDeviceModel,
	getProductTypeDisplayName,
} from "Infrastructure/Utils/Nsure/ProductUtils";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { BlTooltip } from "Shared/BlTooltip";

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	form: DeviceForm;
	setForm: React.Dispatch<React.SetStateAction<DeviceForm>>;
	showInfoWarningColumns?: boolean;
};

const DeviceFormComponent: FunctionComponent<Props> = ({
	deviceStatuses,
	deviceStatus,
	form,
	setForm,
	showInfoWarningColumns = false,
}) => {
	const [categoryFilter, setCategoryFilter] = useState<DeviceCategory | "all">(
		"all"
	);
	const [serverFilter, setServerFilter] = useState<string>("all");
	const [selectedInstitution, setSelectedInstitution] =
		useState<InstitutionDeviceModel | null>(null);
	const [selectedProducts, setSelectedProducts] = useState<
		ProductDeviceModel[]
	>([]);

	const devices = useMemo(() => {
		if (!form?.devices) return [];

		return [...form.devices].sort((a, b) => {
			// First sort by isDisabled
			if (a.isDisabled !== b.isDisabled) {
				return a.isDisabled ? 1 : -1;
			}
			// Then sort by deviceCategory
			if (a.deviceStatus.deviceCategory !== b.deviceStatus.deviceCategory) {
				return a.deviceStatus.deviceCategory - b.deviceStatus.deviceCategory;
			}
			// Finally sort by name
			return a.name.localeCompare(b.name);
		});
	}, [form?.devices]);

	const selectedDevices = useMemo(() => {
		if (!form?.devices) return [];
		return form.devices.filter((device) => device.isChecked);
	}, [form?.devices]);

	const productDeviceModel = useMemo(() => {
		if (!deviceStatuses) return null;
		return mergeDeviceStatuses(deviceStatuses, deviceStatus);
	}, [deviceStatuses, deviceStatus]);

	const availableInstitutions = useMemo(() => {
		if (!productDeviceModel?.institutions) return [];

		const uniqueInstitutions = new Map(
			productDeviceModel.institutions.map((inst) => [
				inst.institution.institutionID,
				inst,
			])
		);

		return Array.from(uniqueInstitutions.values());
	}, [productDeviceModel]);

	const availableProducts = useMemo(() => {
		if (!productDeviceModel) return [];
		if (!selectedInstitution) return productDeviceModel.products;

		return productDeviceModel.products.filter(
			(product) =>
				product.product.institutionID ===
				selectedInstitution.institution.institutionID
		);
	}, [productDeviceModel, selectedInstitution]);

	const availableServers = useMemo(() => {
		if (!form?.devices) return ["all"];
		const servers = new Set(
			form.devices.map(
				(device) => device.deviceStatus.deviceServer ?? "Neznámý"
			)
		);
		return ["all", ...Array.from(servers)].sort();
	}, [form?.devices]);

	const filteredDevices = useMemo(() => {
		if (!devices) return [];

		return devices.filter((device) => {
			// Category and server filters
			const matchesCategory =
				categoryFilter === "all" ||
				device.deviceStatus.deviceCategory === categoryFilter;
			const matchesServer =
				serverFilter === "all" ||
				device.deviceStatus.deviceServer === serverFilter;

			// Product filter
			let matchesProducts = true;
			if (selectedProducts.length > 0) {
				matchesProducts = selectedProducts.every((selectedProduct) => {
					const productStatus = productDeviceModel?.products.find(
						(p) => p.product.productID === selectedProduct.product.productID
					);
					const deviceProductStatus = productStatus?.devices.find(
						(d) => d.deviceName === device.deviceStatus.deviceName
					);
					return deviceProductStatus?.isEnabled ?? false;
				});
			}

			return matchesCategory && matchesServer && matchesProducts;
		});
	}, [
		devices,
		categoryFilter,
		serverFilter,
		selectedProducts,
		productDeviceModel,
	]);

	const handleChange = (_event: any, newValue: DeviceFormItem[]) => {
		setForm((prev) => ({
			...prev,
			devices: prev.devices.map((device) => ({
				...device,
				isChecked: newValue.some((selected) => selected.name === device.name),
			})),
		}));
	};

	const isFiltering =
		categoryFilter !== "all" ||
		serverFilter !== "all" ||
		selectedProducts.length > 0;

	const handleClearFilters = () => {
		setCategoryFilter("all");
		setServerFilter("all");
		setSelectedProducts([]);
	};

	const gridTemplateColumns = showInfoWarningColumns
		? "20px 20px 1fr 100px 100px 250px"
		: "20px 20px 1fr 100px";

	// biome-ignore lint/correctness/useExhaustiveDependencies: gridTemplateColumns is derived from showInfoWarningColumns prop
	const CustomListbox = React.useCallback(
		React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
			(props, ref) => (
				<ul {...props} ref={ref}>
					<li
						style={{
							padding: "4px 14px",
							borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
							position: "sticky",
							top: 0,
							backgroundColor: "white",
							zIndex: 2,
							display: "grid",
							gridTemplateColumns,
							gap: "12px",
							fontSize: "0.75rem",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						<span>Stav</span>
						<span>Typ</span>
						<span>Instance</span>
						<span>Server</span>
						{showInfoWarningColumns && (
							<>
								<span>Notifikace</span>
								<span>Příjemci</span>
							</>
						)}
					</li>
					{props.children}
				</ul>
			)
		),
		[gridTemplateColumns]
	);

	const renderOption = (props: any, option: DeviceFormItem) => (
		<li
			{...props}
			style={{
				opacity: option.isDisabled ? 0.5 : 1,
				cursor: option.isDisabled ? "not-allowed" : "pointer",
				padding: "4px 14px",
				minHeight: 29,
				display: "grid",
				gridTemplateColumns,
				gap: "12px",
				alignItems: "center",
			}}
		>
			<Stack alignItems="center" justifyContent="center">
				<StatusBadge
					statusColor={getDeviceStateColor(option.deviceStatus.deviceState)}
					anchorVertical="bottom"
					anchorHorizontal="right"
					disableAnimation={true}
				>
					<div style={{ width: 8, height: 8 }} />
				</StatusBadge>
			</Stack>
			<Stack alignItems="center" justifyContent="center">
				{option.deviceStatus.deviceCategory === DeviceCategory.Internal && (
					<EngineeringIcon sx={{ fontSize: 16 }} />
				)}
				{option.deviceStatus.deviceCategory === DeviceCategory.Demo && (
					<CoPresentIcon sx={{ fontSize: 16 }} />
				)}
				{option.deviceStatus.deviceCategory === DeviceCategory.Client &&
					(option.deviceStatus.isSelfInsurance ? (
						<PersonIcon sx={{ fontSize: 16 }} />
					) : (
						<GroupIcon sx={{ fontSize: 16 }} />
					))}
			</Stack>
			<span>{option.name}</span>
			<span style={{ color: "text.secondary" }}>
				{option.deviceStatus.deviceServer ?? "Neznámý"}
			</span>
			{showInfoWarningColumns && (
				<>
					<span>
						{option.deviceStatus.infoWarningNotificationEmailEnabled ? (
							<CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
						) : (
							<CancelIcon sx={{ fontSize: 16, color: "error.main" }} />
						)}
					</span>
					<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
						{option.deviceStatus.infoWarningNotificationEmailRecipients || (
							<span style={{ fontStyle: "italic" }}>Žádný</span>
						)}
					</span>
				</>
			)}
		</li>
	);

	// Add memo for joined product data
	const productsWithTypes = useMemo(() => {
		if (!productDeviceModel) return [];
		return productDeviceModel.products.map((product) => ({
			...product,
			productType: productDeviceModel.productTypes.find(
				(pt) => pt.productType.productTypeID === product.product.productTypeID
			)?.productType,
		}));
	}, [productDeviceModel]);

	const sortedProducts = useMemo(() => {
		if (!availableProducts) return [];

		return [...availableProducts].sort((a, b) => {
			// Get product types
			const typeA = productsWithTypes.find(
				(p) => p.product.productID === a.product.productID
			)?.productType;
			const typeB = productsWithTypes.find(
				(p) => p.product.productID === b.product.productID
			)?.productType;

			// Compare product type IDs first
			const typeIdA = typeA?.productTypeID ?? 0;
			const typeIdB = typeB?.productTypeID ?? 0;

			if (typeIdA !== typeIdB) {
				return typeIdA - typeIdB;
			}

			// If same type, compare product names
			return (a.product.name ?? "").localeCompare(b.product.name ?? "");
		});
	}, [availableProducts, productsWithTypes]);

	const ProductListbox = React.useCallback(
		React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
			(props, ref) => (
				<ul {...props} ref={ref} style={{ padding: 0, maxHeight: 490 }}>
					<li
						style={{
							padding: "4px 14px",
							borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
							position: "sticky",
							top: 0,
							backgroundColor: "white",
							zIndex: 2,
							display: "grid",
							gridTemplateColumns: "1fr 0.5fr",
							gap: "12px",
							fontSize: "0.75rem",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						<span>Produkt</span>
						<span>Typ</span>
					</li>
					{props.children}
				</ul>
			)
		),
		[]
	);

	const productAutocompleteRef = useRef<HTMLDivElement>(null);

	if (!devices.length) {
		return (
			<Typography variant="caption">Žádné instance k dispozici</Typography>
		);
	}

	return (
		<Stack spacing={1}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Stack direction="row" spacing={0.5} pl={0.5} alignItems="center">
					<IconButton
						size="small"
						onClick={isFiltering ? handleClearFilters : undefined}
						disabled={!isFiltering}
					>
						{isFiltering ? (
							<FilterAltOffIcon sx={{ fontSize: "1.2rem" }} />
						) : (
							<FilterAltIcon sx={{ fontSize: "1.2rem" }} />
						)}
					</IconButton>
				</Stack>
				<Divider orientation="vertical" flexItem />
				<FormControl size="small" sx={{ minWidth: 100 }}>
					<InputLabel>Typ</InputLabel>
					<Select
						value={categoryFilter}
						label="Typ"
						onChange={(e) =>
							setCategoryFilter(e.target.value as DeviceCategory | "all")
						}
						MenuProps={{
							PaperProps: {
								sx: {
									"& .MuiMenuItem-root": {
										padding: "4px 14px",
										minHeight: "auto",
									},
								},
							},
						}}
					>
						<MenuItem value="all">Vše</MenuItem>
						<MenuItem value={DeviceCategory.Client}>Klient</MenuItem>
						<MenuItem value={DeviceCategory.Internal}>Interní</MenuItem>
						<MenuItem value={DeviceCategory.Demo}>Demo</MenuItem>
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 100 }}>
					<InputLabel>Server</InputLabel>
					<Select
						value={serverFilter}
						label="Server"
						onChange={(e) => setServerFilter(e.target.value)}
						MenuProps={{
							PaperProps: {
								sx: {
									"& .MuiMenuItem-root": {
										padding: "4px 14px",
										minHeight: "auto",
									},
								},
							},
						}}
					>
						{availableServers.map((server) => (
							<MenuItem key={server} value={server}>
								{server === "all" ? "Vše" : server}
							</MenuItem>
						))}
					</Select>
					<Divider orientation="vertical" flexItem />
				</FormControl>
				<Divider orientation="vertical" flexItem />
				<Autocomplete<InstitutionDeviceModel, false>
					size="small"
					options={availableInstitutions}
					value={selectedInstitution}
					onChange={(_, newValue) => {
						setSelectedInstitution(newValue);
						if (newValue && sortedProducts.length > 0) {
							const productInput = document.querySelector<HTMLElement>(
								'input[aria-label="Vyberte produkty"]'
							);
							if (productInput) {
								productInput.focus();
								// Trigger the Autocomplete to open
								const autocompleteInstance =
									productAutocompleteRef.current?.querySelector(
										".MuiAutocomplete-popupIndicator"
									) as HTMLElement;
								autocompleteInstance?.click();
							}
						}
					}}
					noOptionsText="Žádné možnosti"
					getOptionLabel={(option) => {
						const displayName =
							option.institution.displayName ?? option.institution.name ?? "";
						const name = option.institution.name;
						return displayName === name
							? displayName
							: `${displayName} (${name})`;
					}}
					isOptionEqualToValue={(option, value) =>
						option.institution.institutionID === value.institution.institutionID
					}
					renderOption={(props, option) => {
						const displayName =
							option.institution.displayName ?? option.institution.name ?? "";
						const name = option.institution.name;
						return (
							<li {...props} key={option.institution.institutionID}>
								{displayName === name
									? displayName
									: `${displayName} (${name})`}
							</li>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Instituce"
							size="small"
							sx={{
								minWidth: 600,
								"& .MuiInputBase-root": {
									padding: "4px 14px",
								},
							}}
						/>
					)}
					ListboxProps={{
						style: {
							maxHeight: 486,
						},
					}}
				/>
				<ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
				<Autocomplete<ProductDeviceModel, true>
					ref={productAutocompleteRef}
					multiple
					disableCloseOnSelect
					size="small"
					options={sortedProducts}
					value={selectedProducts}
					onChange={(_, newValue) => setSelectedProducts(newValue)}
					noOptionsText="Žádné možnosti"
					getOptionLabel={(option) => option.product.name ?? ""}
					isOptionEqualToValue={(option, value) =>
						option.product.productID === value.product.productID
					}
					sx={{
						flex: 1,
						minWidth: 0,
						"& .MuiInputBase-root": {
							flexWrap: "nowrap",
							overflow: "hidden",
							paddingRight: "32px",
						},
						"& .MuiInputBase-input": {
							width: "0 !important",
							minWidth: "32px",
						},
					}}
					ListboxComponent={ProductListbox}
					renderTags={(tagValue, getTagProps) => (
						<Stack
							direction="row"
							spacing={0.5}
							sx={{
								overflowX: "auto",
								"&::-webkit-scrollbar": {
									display: "none",
								},
								scrollbarWidth: "none",
								msOverflowStyle: "none",
								flex: "1 1 auto",
								width: "calc(100% - 32px)",
							}}
						>
							{tagValue.map((option, index) => (
								<Chip
									{...getTagProps({ index })}
									key={option.product.productID}
									label={option.product.name}
									size="small"
									sx={{
										height: 20,
										maxWidth: 150,
										flexShrink: 0,
										"& .MuiChip-label": {
											fontSize: "0.75rem",
											padding: "0 8px",
											overflow: "hidden",
											textOverflow: "ellipsis",
										},
									}}
								/>
							))}
						</Stack>
					)}
					renderOption={(props, option) => {
						const productType = productsWithTypes.find(
							(p) => p.product.productID === option.product.productID
						)?.productType;

						return (
							<li
								{...props}
								key={option.product.productID}
								style={{
									padding: "4px 14px",
									display: "grid",
									gridTemplateColumns: "1fr 0.5fr",
									gap: "12px",
									alignItems: "center",
									minHeight: "auto",
								}}
							>
								<span>{option.product.name}</span>
								<span style={{ color: "text.secondary" }}>
									{productType
										? getProductTypeDisplayName(productType.productTypeID) ??
										  "Neznámý"
										: "Neznámý"}
								</span>
							</li>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								selectedInstitution && selectedProducts.length === 0
									? "Vyberte produkty"
									: "Produkty"
							}
							placeholder={selectedProducts.length ? "" : "Vyberte produkty"}
							inputProps={{
								...params.inputProps,
								"aria-label": "Vyberte produkty",
							}}
							sx={{
								"& .MuiInputBase-root": {
									padding: "0 14px",
								},
								"& .MuiInputBase-input": {
									padding: "4px 0",
								},
								"& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
									transform: "translate(14px, 8px)",
								},
								"& .MuiAutocomplete-endAdornment": {
									background: "white",
									marginRight: "-8px",
									paddingRight: "8px",
									borderRadius: "25%",
								},
								...(selectedInstitution &&
									selectedProducts.length === 0 && {
										"& .MuiInputLabel-root": {
											color: "primary.main",
										},
									}),
							}}
						/>
					)}
				/>
			</Stack>

			<Stack direction="row" spacing={1} alignItems="center" pl={0.5}>
				<BlTooltip title="Vybrat všechny dostupné instance" enterDelay={1000}>
					<IconButton
						size="small"
						disabled={!filteredDevices.length}
						onClick={() => {
							const selectableDevices = filteredDevices.filter(
								(device) => !device.isDisabled
							);
							setForm((prev) => ({
								...prev,
								devices: prev.devices.map((device) => ({
									...device,
									isChecked:
										device.isChecked ||
										selectableDevices.some((d) => d.name === device.name),
								})),
							}));
						}}
					>
						<DoneAllIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</BlTooltip>
				<Divider orientation="vertical" flexItem />
				<Autocomplete<DeviceFormItem, true>
					multiple
					size="small"
					disableCloseOnSelect
					options={filteredDevices}
					value={selectedDevices}
					onChange={handleChange}
					sx={{ flex: 1 }}
					noOptionsText="Žádné možnosti"
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => option.name === value.name}
					getOptionDisabled={(option) => option.isDisabled}
					ListboxProps={{
						style: {
							maxHeight: 507,
							padding: 0,
							backgroundColor: "white",
						},
					}}
					renderOption={renderOption}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Vyberte instance"
							placeholder={selectedDevices.length ? "" : "Vyberte instance"}
							sx={{
								"& .MuiInputBase-root": {
									minHeight: 40,
									padding: "0 14px",
								},
								"& .MuiInputBase-input": {
									padding: "8.5px 0",
								},
								"& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
									transform: "translate(14px, 10px)",
								},
							}}
						/>
					)}
					ListboxComponent={CustomListbox}
					renderTags={(tagValue, getTagProps) =>
						tagValue.map((option, index) => (
							<Chip
								{...getTagProps({ index })}
								label={option.name}
								size="small"
							/>
						))
					}
				/>
			</Stack>

			<Stack direction="row" spacing={2} pl={6}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ userSelect: "none" }}
				>
					Filtrováno:{" "}
					{filteredDevices.length}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ userSelect: "none" }}
				>
					Dostupných:{" "}
					{filteredDevices.filter((device) => !device.isDisabled).length}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ userSelect: "none" }}
				>
					Vybráno: {selectedDevices.length}
				</Typography>
			</Stack>
		</Stack>
	);
};

export default DeviceFormComponent;
